import { type FC, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { type OptionItem, OptionType, useGetOptionsQuery } from "@/api/admin";
import {
  type AssignedRole,
  useGetUserAssignedRoleByIdQuery,
  UserLevel,
} from "@/api/admin/users";
import {
  Loader,
  TableCellBadge,
  TableCellText,
  TableHeadCellCustom,
  type TableHeaderType,
} from "@/components/atoms";
import {
  DropDownViewMenu,
  FilterSearch,
  type SubMenuConfig,
} from "@/components/molecules";
import { formatEnumValue } from "@/utils/formatEnumValue";
import { getIdsForFilter } from "@/utils/getIdsForFilter";
import { useGroupedRoles } from "@/utils/hooks/useGroupedRoles";
import { useTableParams } from "@/utils/hooks/useTableParams";
import { useVisibleFlags } from "@/utils/hooks/useVisibleFlags";

import { AssignRole, EditRole, UnassignRole } from "../../AssignRole";
import DropDownMenu from "../DropDownMenu";
import DynamicTable from "../DynamicTable";
import { type ColumnConfig } from "../type";

enum AccountUsersAssignedRoleColumn {
  LEVEL = "Level",
  NAME = "Name",
  ROLE = "Role",
  ACTIONS = "",
}

enum Filters {
  Groups = "Groups",
  Brands = "Brands",
  Branches = "Branches",
}

export interface GroupedRole {
  level: string;
  roles: { id: string; name: string }[];
}

const TABLE_HEADERS: TableHeaderType = Object.values(
  AccountUsersAssignedRoleColumn
).filter(Boolean);

type ExtendedUserType = AssignedRole & { actions: string };

interface AccountUsersAssignedRoleTableProps {
  userName: string;
  hasUpdatePermission?: boolean;
}

const NO_ROLE = { id: null, name: "No role" };

const AccountUsersAssignedRoleTable: FC<AccountUsersAssignedRoleTableProps> = ({
  userName,
  hasUpdatePermission = true,
}) => {
  const { id = "" } = useParams();
  const { visibleColumns, setVisibleColumns, flags } =
    useVisibleFlags(TABLE_HEADERS);

  const [searchValue, setSearchValue] = useState("");

  const { tableParams, handleNext, handlePrev, setTableParams } =
    useTableParams(
      {
        initialParams: { query: "" },
      },
      searchValue
    );

  const { groupedRoles, isRolePending } = useGroupedRoles();

  const allRoles = useMemo(
    () => [NO_ROLE, ...groupedRoles.flatMap(group => group.roles)],
    [groupedRoles]
  );

  const { data: usersData, isPending: isPending } =
    useGetUserAssignedRoleByIdQuery(id, tableParams);

  const { data: optionsData, isPending: isOptionPending } = useGetOptionsQuery([
    OptionType.Groups,
    OptionType.Brands,
    OptionType.Branches,
  ]);
  const { data: allUsersData } = useGetUserAssignedRoleByIdQuery(id);

  const filteredOptions = useMemo(() => {
    if (!optionsData || !allUsersData?.items) {
      return { groups: [], brands: [], branches: [] };
    }

    const entityIdsByLevel = {
      [UserLevel.GROUP]: new Set<string>(),
      [UserLevel.BRAND]: new Set<string>(),
      [UserLevel.BRANCH]: new Set<string>(),
    };

    allUsersData.items.forEach(user => {
      if (user.entityId) {
        entityIdsByLevel[user.level].add(user.entityId);
      }
    });

    return {
      groups:
        optionsData.groups?.filter(o =>
          entityIdsByLevel[UserLevel.GROUP].has(o.id)
        ) ?? [],
      brands:
        optionsData.brands?.filter(o =>
          entityIdsByLevel[UserLevel.BRAND].has(o.id)
        ) ?? [],
      branches:
        optionsData.branches?.filter(o =>
          entityIdsByLevel[UserLevel.BRANCH].has(o.id)
        ) ?? [],
    };
  }, [optionsData, allUsersData?.items]);

  const SUB_MENUS = {
    [Filters.Groups]: filteredOptions?.groups ?? [],
    [Filters.Brands]: filteredOptions?.brands ?? [],
    [Filters.Branches]: filteredOptions?.branches ?? [],
    [AccountUsersAssignedRoleColumn.ROLE]: allRoles,
  };

  const MENU_LIST = Object.keys(SUB_MENUS);

  const SUB_MENU_REGISTRY: Record<string, SubMenuConfig> = {
    [Filters.Groups]: {
      type: "search",
      placeholder: "Search groups",
    },
    [Filters.Brands]: {
      type: "search",
      placeholder: "Search brands",
    },
    [Filters.Branches]: {
      type: "search",
      placeholder: "Search branches",
    },
    [AccountUsersAssignedRoleColumn.ROLE]: {
      type: "search",
      placeholder: "Search roles",
    },
  };

  const handleSetFilter = (
    selectedMap: Record<string, string[] | OptionItem[]>
  ) => {
    const selectedGroups = getIdsForFilter(selectedMap[Filters.Groups]);
    const selectedBrands = getIdsForFilter(selectedMap[Filters.Brands]);
    const selectedBranches = getIdsForFilter(selectedMap[Filters.Branches]);

    const selectedRoles = getIdsForFilter(
      selectedMap[AccountUsersAssignedRoleColumn.ROLE]
    );

    setTableParams(prev => ({
      ...prev,
      groupIds: selectedGroups.length ? selectedGroups : undefined,
      brandIds: selectedBrands.length ? selectedBrands : undefined,
      branchIds: selectedBranches.length ? selectedBranches : undefined,
      roleIds: selectedRoles.length ? selectedRoles : undefined,
      offset: 0,
    }));
  };

  const actionColumn: ColumnConfig<ExtendedUserType> | null =
    hasUpdatePermission
      ? {
          key: "actions",
          label: AccountUsersAssignedRoleColumn.ACTIONS,
          visible: true,
          headerComponent: ({ label }) => (
            <TableHeadCellCustom>{label}</TableHeadCellCustom>
          ),
          cellRenderer: (row, isLast) => (
            <TableCellText classes="w-[3%]" last={isLast}>
              <DropDownMenu
                isVertical={false}
                menuList={[
                  {
                    dialog: (
                      <EditRole
                        data={row}
                        rolesList={
                          groupedRoles.find(el => el.level === row.level)
                            ?.roles || []
                        }
                      />
                    ),
                  },
                  {
                    dialog: <UnassignRole data={row} userName={userName} />,
                  },
                ]}
              />
            </TableCellText>
          ),
        }
      : null;

  const columns: ColumnConfig<ExtendedUserType>[] = [
    {
      key: "level",
      label: AccountUsersAssignedRoleColumn.LEVEL,
      visible: flags[AccountUsersAssignedRoleColumn.LEVEL],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {formatEnumValue(row?.level) ?? "-"}
        </TableCellText>
      ),
    },
    {
      key: "name",
      label: AccountUsersAssignedRoleColumn.NAME,
      visible: flags[AccountUsersAssignedRoleColumn.NAME],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellBadge last={isLast} stage="outline">
          {row?.name ?? "-"}
        </TableCellBadge>
      ),
    },
    {
      key: "roleName",
      label: AccountUsersAssignedRoleColumn.ROLE,
      visible: flags[AccountUsersAssignedRoleColumn.ROLE],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) =>
        row.roleName ? (
          <TableCellBadge last={isLast} stage="outline">
            {row?.roleName ?? "-"}
          </TableCellBadge>
        ) : (
          <TableCellText last={isLast}>-</TableCellText>
        ),
    },
    ...(actionColumn ? [actionColumn] : []),
  ];

  return (
    <>
      <FilterSearch
        menuList={MENU_LIST}
        subMenus={SUB_MENUS}
        subMenuRegistry={SUB_MENU_REGISTRY}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        handleSetFilter={handleSetFilter}
        resetObject={NO_ROLE}
      >
        <DropDownViewMenu
          viewList={TABLE_HEADERS}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
        />
        {hasUpdatePermission && (
          <AssignRole
            currentAssignments={usersData?.items || []}
            groupedRoles={groupedRoles}
          />
        )}
      </FilterSearch>

      {isPending || isOptionPending || isRolePending ? (
        <Loader />
      ) : (
        <DynamicTable<AssignedRole>
          data={usersData?.items || []}
          columns={columns as ColumnConfig<AssignedRole>[]}
          searchValue={searchValue}
          next={handleNext}
          prev={handlePrev}
          tableParams={usersData}
          setTableParams={setTableParams}
        />
      )}
    </>
  );
};

export default AccountUsersAssignedRoleTable;
